import React, { useState, useEffect } from 'react';
import {
  ComposedChart,
  XAxis,
  YAxis,
  Bar,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { Select, Title, Box, Group, useMantineTheme } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';

export default function ActionGanttChart({
  topics,
  actions,
  sessions,
  topicLevels,
  statusColors,
}) {
  const theme = useMantineTheme();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [selectedTopic, setSelectedTopic] = useState(
    isMobile && topics.length ? topics[0].name : 'All'
  );

  useEffect(() => {
    if (isMobile && selectedTopic === 'All' && topics.length > 0) {
      setSelectedTopic(topics[0].name);
    } else if (
      !isMobile &&
      !['All', ...topics.map((t) => t.name)].includes(selectedTopic)
    ) {
      setSelectedTopic('All');
    }
  }, [isMobile, selectedTopic, topics]);

  const filteredTopics =
    selectedTopic === 'All'
      ? topics
      : topics.filter((t) => t.name === selectedTopic);

  const filteredActions =
    selectedTopic === 'All'
      ? actions
      : actions.filter((a) => a.topicName === selectedTopic);

  const extendedSessions = sessions;

  const ROW_HEIGHT =
    filteredTopics.length > 3 ? (isMobile ? 50 : 80) : isMobile ? 50 : 60;
  const BAR_THICKNESS = 10;
  const BAR_SPACING = 6;
  const statusCounts = {};

  const data = filteredTopics.map((topic, rowIdx) => {
  const row = { label: topic.name, competency: topicLevels[topic.level] };
  const topicActions = filteredActions
    .filter((a) => a.topicName === topic.name)
    .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

  const numActions = topicActions.length;
  const totalBarsHeight =
    BAR_THICKNESS * numActions + BAR_SPACING * (numActions - 1);
  const rowCenterOffset = (ROW_HEIGHT - totalBarsHeight) / 2;
  row._barOffsets = [];

  topicActions.forEach((action, idx) => {
    const actionTs = new Date(action.timestamp);

  const actionStart = new Date(action.date);
  const actionEnd = new Date(action.updatedAt ?? action.date);


  let createdIdx = -1;
  for (let i = sessions.length - 1; i >= 0; i--) {
    if (new Date(sessions[i].timestamp) <= actionStart) {
      createdIdx = i;
      break;
    }
  }
  if (createdIdx === -1) return;

  let updatedIdx = createdIdx;
  for (let i = sessions.length - 1; i >= 0; i--) {
    if (new Date(sessions[i].timestamp) <= actionEnd) {
      updatedIdx = i;
      break;
    }
  }

  let len = updatedIdx - createdIdx;
  const isSameSession = len === 0;

  if (isSameSession) {
    len = 0.10; 
  }
  const durKey = `R${rowIdx}_S${createdIdx + 1}_${idx}_duration`;
  const statusKey = `R${rowIdx}_S${createdIdx + 1}_${idx}_status`;
  row[durKey] = len;
  row[statusKey] = action.status;

  extendedSessions.forEach((_, i) => {
    const offKey = `R${rowIdx}_S${i + 1}_${idx}_offset`;
    row[offKey] = i === createdIdx ? createdIdx + 1 : 0;
  });

  const norm = Object.keys(statusColors).find(
    (k) => k.toLowerCase() === action.status.toLowerCase()
  );
  statusCounts[norm || action.status] =
    (statusCounts[norm || action.status] || 0) + 1;

  const barOffset = rowCenterOffset + idx * (BAR_THICKNESS + BAR_SPACING) + (isSameSession ? 1.5 : 0);
  row._barOffsets.push(barOffset);
  });

  return row;
});

const hasBars = data.some((row) =>
  Object.keys(row).some((k) => k.includes('_duration'))
);

const getColor = (status) => {
  const key = statusColors[status];
  return theme.colors[key]?.[8] || theme.colors.gray[4];
};

  return (
    <>
      <Title order={5} tt="uppercase">
        Actions Timeline
      </Title>
      <Select
        label="Filter by Topic"
        data={isMobile ? topics.map((t) => t.name) : ['All', ...topics.map((t) => t.name)]}
        value={selectedTopic}
        onChange={setSelectedTopic}
        mt="md"
        mb="md"
        style={{ width: 300, fontSize: 1, fontFamily: 'Inter, sans-serif' }}
      />

      {hasBars ? (
        <>
          {!isMobile && (
            <Group spacing="xs" mt={4} wrap="wrap">
              {Object.keys(statusColors).map((status) => {
                const count = statusCounts[status] || 0;
                return (
                  <Box
                    key={status}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginRight: 12,
                    }}
                  >
                    <Box
                      style={{
                        width: 14,
                        height: 14,
                        backgroundColor: getColor(status),
                        borderRadius: 3,
                        marginRight: 6,
                      }}
                    />
                    <Title color="gray" order={6} fw={600}>
                      {status} ({count})
                    </Title>
                  </Box>
                );
              })}
            </Group>
          )}

          <ResponsiveContainer width="100%" height={data.length * ROW_HEIGHT + 80}>
            <ComposedChart layout="vertical" data={data} barCategoryGap={0} barGap={0}>
              <XAxis
                type="number"
                domain={[0.5, extendedSessions.length + 0.5]}
                ticks={extendedSessions.map((_, i) => i + 1)}
                tickFormatter={(i) => `S${i}`}
                tickLine={false}
                axisLine={{ strokeWidth: 0.5 }}
              />
              <YAxis
                dataKey="label"
                type="category"
                width={isMobile ? 0 : 220}
                tickLine={false}
                axisLine={{ stroke: '#ccc', strokeWidth: 1 }}
                tick={!isMobile && (({ x, y, payload }) => {
                  const topic = payload.value;
                  const row = data.find((d) => d.label === topic);
                  const level = topicLevels.indexOf(row?.competency);
                  return (
                    <g transform={`translate(${x}, ${y})`}>
                      <foreignObject x={-220} y={-20} width={220} height={60}>
                        <Box
                          xmlns="http://www.w3.org/1999/xhtml"
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-end',
                            fontSize: 13,
                            fontFamily: 'Inter, sans-serif',
                            color: '#333',
                            wordWrap: 'break-word',
                            whiteSpace: 'normal',
                            textAlign: 'right',
                          }}
                        >
                          <Title order={6} fw={500} style={{ lineHeight: '1.3em', marginBottom: 4 }}>
                            {topic}
                          </Title>
                          <Box style={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                            {topicLevels.map((_, i) => (
                              <Box
                                key={i}
                                w={16}
                                h={16}
                                style={{
                                  borderRadius: 2,
                                  backgroundColor: i <= level ? theme.colors.green[6] : 'transparent',
                                  border: `1px solid ${i <= level ? theme.colors.green[6] : 'gray'}`,
                                }}
                              />
                            ))}
                          </Box>
                        </Box>
                      </foreignObject>
                    </g>
                  );
                })}
              />
              {data.map((row, rowIdx) =>
                Object.keys(row)
                  .filter((k) => k.includes('_duration'))
                  .map((durKey) => {
                    const parts = durKey.split('_');
                    const idx = parseInt(parts[2]);
                    const offKey = `R${rowIdx}_${parts[1]}_${idx}_offset`;
                    const statusKey = `R${rowIdx}_${parts[1]}_${idx}_status`;
                    const barOffset = row._barOffsets[idx] || 0;
                    const stack = `${durKey}-${rowIdx}-${idx}`;

                    return (
                      <React.Fragment key={stack}>
                        <Bar
                          dataKey={offKey}
                          stackId={stack}
                          fill="transparent"
                          barSize={1}
                          y={barOffset}
                          isAnimationActive={false}
                        />
                        <Bar
                          dataKey={offKey}
                          stackId={`stack-${row.label}-${idx}`}
                          fill="transparent"
                          barSize={BAR_THICKNESS}
                          y={barOffset}
                          isAnimationActive={false}
                        />
                        <Bar
                          dataKey={durKey}
                          stackId={`stack-${row.label}-${idx}`}
                          barSize={BAR_THICKNESS}
                          y={barOffset}
                          radius={[4, 4, 4, 4]}
                          isAnimationActive={false}
                        >
                          {data.map((_, i) => (
                            <Cell
                              key={`cell-${i}`}
                              fill={getColor(row[statusKey] || 'Opened')}
                            />
                          ))}
                        </Bar>
                      </React.Fragment>
                    );
                  })
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </>
      ) : (
        <Box>
          <Title color="gray" order={6}>
            No sessions available
          </Title>
        </Box>
      )}
    </>
  );
}